import _ from 'lodash';

import { groupReservations } from '../reservations';
import { PROPERTY_STATUSES } from '../property';
import { getGroupsWithSubGroups } from '../company';

import {
  PROPERTY_FILTERS_NAMES_TRANSLATION,
  READINESS_FILTER_VALUES,
  RESERVATION_FILTERS_VALIDATOR_MAP,
  STATUS_FILTER_VALUES,
} from './constants';

/* HELPERS */

/**
 * @param {String} primaryDate
 * @param {String} secondaryDate
 * @return {Number}
 */
const countReservationDatesDifference = (primaryDate, secondaryDate) =>
  _.size(_.difference(primaryDate, secondaryDate));

/**
 * @param {Array.<String>} checkinDates
 * @param {Array.<String>} checkoutDates
 * @return {Boolean}
 */
const hasCheckinOnly = (checkinDates, checkoutDates) =>
  countReservationDatesDifference(checkinDates, checkoutDates) > 0;

/**
 * @param {Array.<String>} checkinDates
 * @param {Array.<String>} checkoutDates
 * @return {Boolean}
 */
const hasCheckoutOnly = (checkinDates, checkoutDates) =>
  countReservationDatesDifference(checkoutDates, checkinDates) > 0;

/**
 * @param {Object} groupedReservations
 * @return {Object}
 */
const preparePropertyReservationsValidator = groupedReservations => {
  const { backToBack, checkins, checkouts, hasReservation, recent } = groupedReservations;

  const isBackToBack = _.size(_.get(backToBack, 'all', [])) > 0;
  const isBackToBackOwner = _.size(_.get(backToBack, 'owner', [])) > 0;
  const isBackToBackGuest = _.size(_.get(backToBack, 'guest', [])) > 0;
  const isCheckinsOwner = _.size(_.get(checkins, 'owner', [])) > 0;
  const isCheckoutsOwner = _.size(_.get(checkouts, 'owner', [])) > 0;
  const isCheckinsGuest = _.size(_.get(checkins, 'guest', [])) > 0;
  const isCheckoutsGuest = _.size(_.get(checkouts, 'guest', [])) > 0;
  const isRecent = _.size(recent) > 0;
  const isCheckinOnly = hasCheckinOnly(checkins.allDates, checkouts.allDates);
  const isCheckoutOnly = hasCheckoutOnly(checkins.allDates, checkouts.allDates);
  const isHold = !(
    _.isEmpty(_.get(checkins, 'hold', [])) && _.isEmpty(_.get(checkouts, 'hold', []))
  );
  const hasNoReservation = !hasReservation;

  return {
    isBackToBack,
    isBackToBackOwner,
    isBackToBackGuest,
    isCheckinOnly,
    isCheckinsOwner,
    isCheckinsGuest,
    isCheckoutOnly,
    isCheckoutsOwner,
    isCheckoutsGuest,
    hasReservation,
    isHold,
    isRecent,
    hasNoReservation,
  };
};

/* FILTERS */

/**
 * @description Filter property by readiness
 * @param {Object} propertyNextReservation
 * @param {Array.<String>} readinessFilters
 * @return {Boolean}
 */
export const filterPropertyByReadiness = (propertyNextReservation, readinessFilters) => {
  const withoutReadinessFilters = _.size(readinessFilters) === 0;
  const includesReadyFilter = _.includes(readinessFilters, READINESS_FILTER_VALUES.READY);
  const includesNotReadyFilter = _.includes(readinessFilters, READINESS_FILTER_VALUES.NOT_READY);
  const nextReservationReadiness = _.get(propertyNextReservation, 'ready', null);

  if (withoutReadinessFilters) {
    return true;
  } else if (includesReadyFilter && includesNotReadyFilter) {
    return nextReservationReadiness !== null;
  }

  const isPropertyReady = includesReadyFilter && nextReservationReadiness === true;
  const isPropertyNotReady = includesNotReadyFilter && nextReservationReadiness === false;

  return isPropertyReady || isPropertyNotReady;
};

/**
 * @description Filter property by building
 * @param {Object} property
 * @param {Array.<Number>} buildingFilters
 * @return {Boolean}
 */
export const filterPropertyByBuilding = (property, buildingFilters) => {
  const propertyBuildingId = _.get(property, 'building_id', null);

  return _.includes(buildingFilters, propertyBuildingId);
};

/**
 * @description Filter property by group
 * @param {Object} property
 * @param {Array.<Number>} groupFilters
 * @param {Object} companyGroups
 * @return {Boolean}
 */
export const filterPropertyByGroup = (property, groupFilters, companyGroups) => {
  const companyGroupId = _.get(property, 'company_group_id', null);
  const filteredGroups = getGroupsWithSubGroups(companyGroups, groupFilters);
  return _.includes(filteredGroups, companyGroupId);
};

/**
 * @description Filter property by tag
 * @param {Object} property
 * @param {Array.<Number>} propertyTags
 * @param {Array.<Number>} tags
 * @return {Boolean}
 */
export const filterPropertyByTag = (property, tagFilters, propertyTags = null) => {
  const hasPropertyTags = !_.isNull(propertyTags);

  if (hasPropertyTags) {
    const commonPropertyTags = _.intersection(tagFilters, propertyTags);

    return _.size(commonPropertyTags) > 0;
  }

  const propertyTagIds = _.get(property, 'tag_ids', []);
  const hasPropertyTagIds = _.size(propertyTagIds) > 0;
  const commonPropertyTagIds = _.intersection(tagFilters, propertyTagIds);

  return hasPropertyTagIds && _.size(commonPropertyTagIds) > 0;
};

/**
 * @description Filter property by type property status code
 * @param {Object} property
 * @param {Array.<String>} statusFilters
 * @return {Boolean}
 */
export const filterPropertyByStatus = (property, statusFilters) => {
  const includesAllStatus = _.includes(statusFilters, STATUS_FILTER_VALUES.ALL);

  if (includesAllStatus) {
    return true;
  }

  const propertyStatusCode = _.get(property, 'type_property_status.code', null);
  return (
    _.includes(statusFilters, propertyStatusCode) ||
    (propertyStatusCode === PROPERTY_STATUSES.CONFLICT &&
      _.includes(statusFilters, PROPERTY_STATUSES.ACTIVE))
  );
};

/**
 * @description Filter property by reservation
 * @param {Array.<Object>} propertyReservations
 * @param {Array.<String>} statusFilters
 * @return {Boolean}
 */
export const filterPropertyByReservation = (
  propertyReservations,
  reservationFilters,
  dateRange,
) => {
  const withoutReservationFilters = _.size(reservationFilters) === 0;

  if (withoutReservationFilters) {
    return true;
  }

  const groupedReservations = groupReservations(propertyReservations, dateRange);
  const propertyReservationsValidator = preparePropertyReservationsValidator(groupedReservations);

  return _.some(reservationFilters, reservationFilter => {
    const reservationCategory = _.get(RESERVATION_FILTERS_VALIDATOR_MAP, reservationFilter);

    return _.get(propertyReservationsValidator, reservationCategory, false);
  });
};

/* PROPERTY FILTERS MAPS */

/**
 * @type {Object}
 */
export const PROPERTY_FILTERS_MAP = {
  [PROPERTY_FILTERS_NAMES_TRANSLATION.BUILDINGS]: filterPropertyByBuilding,
  [PROPERTY_FILTERS_NAMES_TRANSLATION.GROUPS]: filterPropertyByGroup,
  [PROPERTY_FILTERS_NAMES_TRANSLATION.PROPERTY_STATUSES]: filterPropertyByStatus,
  [PROPERTY_FILTERS_NAMES_TRANSLATION.READINESS]: filterPropertyByReadiness,
  [PROPERTY_FILTERS_NAMES_TRANSLATION.RESERVATIONS]: filterPropertyByReservation,
  [PROPERTY_FILTERS_NAMES_TRANSLATION.STATUSES]: filterPropertyByStatus,
  [PROPERTY_FILTERS_NAMES_TRANSLATION.TAGS]: filterPropertyByTag,
};

/* GENERAL */

/**
 * @description Main function for properties and properity relatives filtering
 * @param {Object} propertyFilteringParams
 * @param {Object} propertyFilteringParams.property
 * @param {Array.<Object>} propertyFilteringParams.propertyFilters
 * @param {Array.<Number>} propertyFilteringParams.propertyTags
 * @param {Object} propertyFilteringParams.propertyNextReservation
 * @param {Array.<Object>} propertyFilteringParams.propertyReservations
 * @param {Object} propertyFilteringParams.dateRange
 */
export const shouldPropertyBeFiltered = ({
  property,
  propertyFilters,
  propertyTags,
  propertyNextReservation,
  propertyReservations,
  dateRange,
  companyGroups,
}) =>
  _.some(propertyFilters, propertyFilter => {
    const { filterName, values: filterValues } = propertyFilter;
    const filterFunction = _.get(PROPERTY_FILTERS_MAP, filterName);

    // Trigger filter if function exists
    if (_.isFunction(filterFunction)) {
      // Custom filters with extra data
      if (filterName === PROPERTY_FILTERS_NAMES_TRANSLATION.READINESS) {
        return !filterFunction(propertyNextReservation, filterValues);
      } else if (filterName === PROPERTY_FILTERS_NAMES_TRANSLATION.TAGS) {
        return !filterFunction(property, filterValues, propertyTags);
      } else if (filterName === PROPERTY_FILTERS_NAMES_TRANSLATION.RESERVATIONS) {
        return !filterFunction(propertyReservations, filterValues, dateRange);
      } else if (filterName === PROPERTY_FILTERS_NAMES_TRANSLATION.GROUPS) {
        return !filterFunction(property, filterValues, companyGroups);
      }

      // Default basic filter
      return !filterFunction(property, filterValues);
    }

    // Return false when no filter function - no filtering will cause
    // returning all properties
    return false;
  });
